/**
 * Centralized Google Analytics event tracking utility.
 * All feature usage events should go through this helper.
 */

declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
  }
}

type EventParams = Record<string, string | number | boolean | undefined>;

export function trackEvent(
  eventName: string,
  params?: EventParams
) {
  if (typeof window.gtag === 'undefined') return;

  window.gtag('event', eventName, {
    ...params,
    non_interaction: false,
  });
}

// ── Pre-defined event helpers ──

export const analytics = {
  /** User saves or unsaves a nursing home */
  saveNursingHome(nhSlug: string, centerName: string, action: 'save' | 'unsave', source: string) {
    trackEvent(action === 'save' ? 'save_nursing_home' : 'unsave_nursing_home', {
      nursing_home_id: nhSlug,
      nursing_home_name: centerName,
      source,
    });
  },

  /** User clicks "View Details" on a card */
  viewDetails(nhSlug: string, centerName: string, source: string) {
    trackEvent('view_details', {
      nursing_home_id: nhSlug,
      nursing_home_name: centerName,
      source,
    });
  },

  /** User clicks external website link */
  websiteClick(nhSlug: string, centerName: string, source: string) {
    trackEvent('website_click', {
      nursing_home_id: nhSlug,
      nursing_home_name: centerName,
      source,
    });
  },

  /** User submits a quick enquiry */
  quickEnquiry(nhSlug: string, centerName: string, source: string) {
    trackEvent('quick_enquiry_submit', {
      nursing_home_id: nhSlug,
      nursing_home_name: centerName,
      source,
    });
  },

  /** User opens the quick enquiry form */
  quickEnquiryOpen(nhSlug: string, centerName: string, source: string) {
    trackEvent('quick_enquiry_open', {
      nursing_home_id: nhSlug,
      nursing_home_name: centerName,
      source,
    });
  },

  /** User clicks the saved/favorites indicator in the navbar */
  viewSavedList(savedCount: number, isLoggedIn: boolean) {
    trackEvent('view_saved_list', {
      saved_count: savedCount,
      is_logged_in: isLoggedIn,
    });
  },

  /** User downloads or views a guide */
  guideClick(guideName: string, source: string) {
    trackEvent('guide_click', {
      guide_name: guideName,
      source,
    });
  },

  /** User clicks "Create Account" prompt from save toast */
  signupPromptClick(source: string) {
    trackEvent('signup_prompt_click', { source });
  },

  /** Fair Deal calculator interaction */
  calculatorStep(step: string) {
    trackEvent('calculator_step', { step });
  },

  /** Contact form submission */
  contactFormSubmit(nhSlug: string, source: string) {
    trackEvent('contact_form_submit', {
      nursing_home_id: nhSlug,
      source,
    });
  },

  // ── Care Advisor Chat ──

  /** User opens the Care Advisor page */
  careAdvisorOpen(isLoggedIn: boolean) {
    trackEvent('care_advisor_open', { is_logged_in: isLoggedIn });
  },

  /** User sends their first message (starts a conversation) */
  careAdvisorStart(topic: string, isLoggedIn: boolean) {
    trackEvent('care_advisor_start', { topic, is_logged_in: isLoggedIn });
  },

  /** User sends a message */
  careAdvisorMessage(messageNumber: number, isLoggedIn: boolean) {
    trackEvent('care_advisor_message', { message_number: messageNumber, is_logged_in: isLoggedIn });
  },

  /** User clicks a suggested topic chip */
  careAdvisorTopicClick(topic: string) {
    trackEvent('care_advisor_topic_click', { topic });
  },

  /** Signup nudge shown to anonymous user */
  careAdvisorSignupNudgeShown() {
    trackEvent('care_advisor_signup_nudge_shown');
  },

  /** User clicks signup from nudge */
  careAdvisorSignupClick() {
    trackEvent('care_advisor_signup_click');
  },

  /** Premium nudge shown */
  careAdvisorPremiumNudgeShown() {
    trackEvent('care_advisor_premium_nudge_shown');
  },

  /** User clicks CTA to open Care Advisor */
  careAdvisorCTAClick(source: string) {
    trackEvent('care_advisor_cta_click', { source });
  },
};
